import {
  type ApiTaskSettingsModifiers,
  ApiTaskSettingsModifiersSchema,
  type TaskModifierType,
} from '../ApiSchemas/RecordTypes/ApiTaskSettingsModifiersSchema';

export const DEFERRED_TASK_MODIFIER_KEY = 'deferred';
export const TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY = 'tenantResponsibility';

// Enablement follows the orgSettingsTradeLib convention: absent isEnabled/isEnabledFieldUser
// means enabled. These defaults are injected when a modifier is turned ON (legacy scanEditor
// flag or the Outputs toggle), so they must not bake in a disabled state.
export const DEFAULT_DEFERRED_TASK_MODIFIER: ApiTaskSettingsModifiers = {
  modifierTag: 'deferred',
  displayName: 'Deferred',
  excludeFromPurchaseOrders: true, // documentation only until PO export reads org-settings metadata
  triggeredByChangeOrder: true, // documentation only until change order logic is wired
};

export const DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER: ApiTaskSettingsModifiers = {
  modifierTag: 'tenantResponsibility',
  displayName: 'Resident Responsible',
};

/**
 * Display modifiers carry no financial rules, so their defaults set none — not even `false`.
 * They also have no default tag or label: an org names each one it creates and its tag is minted
 * per modifier, so both come from the record rather than from here. Hence `Partial`, not a record
 * with a placeholder tag — an empty-string default would defeat the `??` fallback below.
 */
export const DEFAULT_DISPLAY_TASK_MODIFIER: Partial<ApiTaskSettingsModifiers> = {};

/** Defaults are per Kai type now that a record's key no longer implies one. */
const TASK_MODIFIER_DEFAULTS_BY_TYPE: Record<TaskModifierType, Partial<ApiTaskSettingsModifiers>> = {
  deferred: DEFAULT_DEFERRED_TASK_MODIFIER,
  resident: DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER,
  display: DEFAULT_DISPLAY_TASK_MODIFIER,
};

/**
 * The two keys that predate org-created modifiers. Their type is inferred when a stored record
 * does not carry one, so rows written before `type` existed normalize without a migration.
 */
const TASK_MODIFIER_TYPE_BY_RESERVED_KEY: Record<string, TaskModifierType> = {
  [DEFERRED_TASK_MODIFIER_KEY]: 'deferred',
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: 'resident',
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

const getModifierType = (
  key: string,
  modifier: ApiTaskSettingsModifiers,
): TaskModifierType | undefined =>
  // The reserved key wins over whatever the client sent. These two keys name the tags that drive
  // change orders and reports, so a record claiming `{ deferred: { type: 'display' } }` would read
  // as harmless while its tag kept triggering change orders.
  TASK_MODIFIER_TYPE_BY_RESERVED_KEY[key] ?? modifier.type;

const getModifierTag = (
  key: string,
  modifier: ApiTaskSettingsModifiers,
  defaults: Partial<ApiTaskSettingsModifiers>,
): string =>
  // A reserved key's tag is forced to the key for the same reason its type is: `changeOrderLib`
  // and the reports branch on these two literals, so a stored tag that drifted from them reads as
  // a tag nothing recognises. Three production rows carry `tenantResponsible`, and their line
  // items carry `tenantResponsibility` — the tag the editors have always written. Forcing the key
  // reunites them instead of stranding both. Every other key keeps the tag it was minted with.
  TASK_MODIFIER_TYPE_BY_RESERVED_KEY[key]
    ? key
    // The tag is the modifier's identity in scan data. A record that carries one keeps it; one
    // that does not falls back to its key, which is what the reserved rows have always used.
    : modifier.modifierTag ?? defaults.modifierTag ?? key;

const mergeModifierWithDefaults = (
  key: string,
  modifier: ApiTaskSettingsModifiers,
): ApiTaskSettingsModifiers | undefined => {
  // An unreserved key with no type is not a modifier this system can reason about: it has no
  // rules to follow and no defaults to fill from. Dropping it is the honest outcome.
  const type = getModifierType(key, modifier);
  if (!type) return undefined;
  const defaults = TASK_MODIFIER_DEFAULTS_BY_TYPE[type];

  const mergedModifier: ApiTaskSettingsModifiers = {
    ...defaults,
    ...modifier,
    type,
    modifierTag: getModifierTag(key, modifier, defaults),
  };

  const parsed = ApiTaskSettingsModifiersSchema.safeParse(mergedModifier);
  return parsed.success ? parsed.data : undefined;
};

/**
 * Default modifier records injected when legacy scanEditor.showLineItemTagsKebabAndMenu is enabled.
 */
export const getDefaultOrgSettingsTaskModifiers = (): Record<string, ApiTaskSettingsModifiers> => ({
  // `type` is written explicitly rather than left to inference, so rows created from here are
  // canonical from the start and do not depend on their key to be understood.
  [DEFERRED_TASK_MODIFIER_KEY]: { ...DEFAULT_DEFERRED_TASK_MODIFIER, type: 'deferred' },
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: { ...DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER, type: 'resident' },
});

/** The labels an org gave its two financial modifiers, as reports and artifacts print them. */
export type TaskModifierDisplayNames = {
  deferred: string;
  tenantResponsibility: string;
};

const getTaskModifierDisplayName = (
  modifiers: Record<string, ApiTaskSettingsModifiers> | undefined,
  key: string,
  defaultModifier: ApiTaskSettingsModifiers,
): string => modifiers?.[key]?.displayName?.trim() || defaultModifier.displayName || key;

/**
 * The labels the org gave its deferred and tenant-responsibility modifiers, falling back to the defaults.
 *
 * Reports and artifacts read these instead of hardcoded labels so they say what the app says.
 */
export const getTaskModifierDisplayNames = (
  modifiers: Record<string, ApiTaskSettingsModifiers> | undefined,
): TaskModifierDisplayNames => ({
  deferred: getTaskModifierDisplayName(modifiers, DEFERRED_TASK_MODIFIER_KEY, DEFAULT_DEFERRED_TASK_MODIFIER),
  tenantResponsibility: getTaskModifierDisplayName(
    modifiers,
    TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY,
    DEFAULT_TENANT_RESPONSIBILITY_TASK_MODIFIER,
  ),
});

/**
 * Normalizes stored or merged taskSettings.modifiers into the canonical record shape.
 *
 * - Fills missing optional fields from the record's type defaults without overwriting org values
 * - Keeps every key the org has configured; drops only entries with no resolvable type
 */
export const normalizeTaskSettingsModifiers = (
  modifiers: unknown,
): Record<string, ApiTaskSettingsModifiers> | undefined => {
  if (!isPlainObject(modifiers)) return undefined;

  const normalizedModifiers: Record<string, ApiTaskSettingsModifiers> = {};

  for (const key of Object.keys(modifiers)) {
    const modifierValue = modifiers[key];
    // modifierTag is derivable from the key (mergeModifierWithDefaults fills it from defaults),
    // so a partial record missing it should still be normalized rather than dropped.
    if (!isPlainObject(modifierValue)) continue;

    const normalizedModifier = mergeModifierWithDefaults(
      key,
      modifierValue as ApiTaskSettingsModifiers,
    );
    if (normalizedModifier) normalizedModifiers[key] = normalizedModifier;
  }

  // Return the (possibly empty) record so "configured, nothing enabled" ({ modifiers: {} }) is
  // representable and distinct from "not configured" (undefined). Callers that received a
  // non-object above already got undefined.
  return normalizedModifiers;
};
